import {useState} from "react"
import {apiCatalog} from "./Api"
import {productImagePlaceholder} from "../settings"

const ATT_ID_COLOR = [221, 222, 235] // Farbe
const ATT_ID_MOUNTING = 225 // Befestigung
const ATT_ID_MOUNTING_OPTION = [
    798, // Montage-/Hängekassette mit Hängesystem Basic
    799 // Montage-/Hängekassette mit Hängesystem Style
]

export const MOUNTING__CEILING = "ceiling"
export const MOUNTING__DOOR = "door"
export const MOUNTING__FLOOR = "floor"
export const MOUNTING__WALL = "wall"
export const MOUNTING__WIRE_DUO40 = "wire-duo-40"
export const MOUNTING__WIRE_DUO48 = "wire-duo-48"
export const MOUNTING__WIRE_UNO = "wire-uno"


export const useCatalog = () => {
    const [products, setProducts] = useState(null)

    const load = async (lang) => {
        try{
            const data = await apiCatalog(lang)
            setProducts(data)
        } catch (error) {
            console.error(error)
        }
    }

    const getImage = sku => {
        const image = products[sku]
            ? products[sku].imageUrl
            : Object.values(products).find(product => !!product.variants[sku])?.imageUrl

        return image ?? productImagePlaceholder
    }

    const getParentSku = sku => {
        return Object.values(products).find(product => !!Object.values(product.variants).find(variant => variant.sku === sku))?.sku
    }

    const isWire = (sku) => {
        return products[sku]
            && products[sku].variants
            && Object.values(products[sku].variants).find(variant => variant.providesMountingSurface !== null)
    }

    const getParentProduct = (parentSku) => {
        return (parentSku && products[parentSku]) ? products[parentSku] : null
    }

    const getVariantProduct = (parentSku, variantSku) => {
        return getParentProduct(parentSku)?.variants[variantSku]
    }

    const getVariantOptions = (parentSku, variantSku) => {
        const parentProduct = getParentProduct(parentSku)
        const variantProduct = getVariantProduct(parentSku, variantSku)

        return parentProduct.configurableOptions.map(attribute => {
            const variantAttributeValue = attribute.values.find(value => value.id === variantProduct.attributes[attribute.id])
            return {
                attributeLabel: attribute.label,
                valueLabel: variantAttributeValue.label,
                valueSwatch: variantAttributeValue.swatch,
            }
        })
    }

    const getVariantColors = (parentSku, variantSku) => {
        const parentProduct = getParentProduct(parentSku)
        const variantProduct = getVariantProduct(parentSku, variantSku)
        const colorAttributes = parentProduct.configurableOptions.filter(attribute => ATT_ID_COLOR.includes(attribute.id))

        return colorAttributes.map(attribute => {
            const variantAttributeValue = attribute.values.find(value => value.id === variantProduct.attributes[attribute.id])
            return {
                attributeLabel: attribute.label,
                valueLabel: variantAttributeValue.label,
                valueSwatch: variantAttributeValue.swatch,
            }
        })
    }

    const getList = (mountingSurfaces, roomHeight, ceilingConstitution, wallConstitution, search) => {

        let list = Object.values(products)

        const wireAll = Object.values(products)
            .filter(product => Object.values(product.variants).find(variant => variant.providesMountingSurface))
            .map(product => product.sku)

        const wireDeny = Object.values(products)
            .filter(product => wireAll.includes(product.sku))
            .filter(product => !(product.allowedCeilingStructures && product.allowedCeilingStructures.includes(ceilingConstitution)))
            .filter(product => !(product.allowedWallStructures && product.allowedWallStructures.includes(wallConstitution)))
            .map(product => product.sku)

        // remove product with missing mountingSurfaces option
        list = list.filter(product => product.mountingSurfaces.find(productMountingSurface => mountingSurfaces.includes(productMountingSurface)))

        // remove product if some variant property minRoomHeight value is greater then roomHeight
        list = list.filter(product => Object.values(product.variants).find(variant => !(variant.minRoomHeight > roomHeight)))

        // remove wires
        list = list.filter(product => !wireAll.includes(product.sku))

        // remove wire mounting with deny wires
        list = list.filter(product => Object.values(product.variants).find(variant => !(wireDeny.includes(variant.wireSku))))

        if (search) {
            const searchReg = new RegExp(search, 'i')
            list = list.filter(product => searchReg.test(product.name) || searchReg.test(product.sku))
        }

        return list
    }

    return {
        load,
        isLoaded: () => products !== null,
        getProducts: () => products,
        getParentProduct,
        getVariantProduct,
        getVariantOptions,
        getVariantColors,
        getChangeList: (mountingSurfaces, roomHeight) => getChangeList(products, mountingSurfaces, roomHeight),
        getList,
        getImage,
        getParentSku,
        isWire,
    }
}


export const getVariantByOptions = (mounting, product, options) => {
    return getVariants(mounting, product).find(variant => {
        for (let optionId of Object.keys(options)) {
            if(options[optionId] === null) continue
            if (options[optionId] !== variant.attributes[optionId]) return false
        }
        return true
    })
}

export const getValidValues = (mounting, product, productOptions, optionId) => {
    const optionIndex = product.configurableOptions.findIndex(option => option.id === optionId)
    const optionIds = product.configurableOptions.map(option => option.id)
    const depOptionIds = optionIds.slice(0, optionIndex)
    const optionsDependency = {}

    optionIds.map(optionId => {
        optionsDependency[optionId] = null
    })

    depOptionIds.map(optionId => {
        optionsDependency[optionId] = productOptions[optionId]
    })

    const validValues = {}

    getVariants(mounting, product).map(variant => {

        let isValidValueId = true

        Object.keys(variant.attributes).forEach(key => {
            if (!(optionsDependency[key] == null || optionsDependency[key] === variant.attributes[key])) {
                isValidValueId = false
            }
        })

        if (isValidValueId) {
            const valueId = variant.attributes[optionId]
            validValues[valueId] = valueId
        }
    })

    return Object.values(validValues)
}


export const getValidOptions = (mounting, product, options) => {
    if (getVariantByOptions(mounting, product, options))
        return {...getVariantByOptions(mounting, product, options).attributes}

    for (let option of Object.values(product.configurableOptions).reverse()) {
        options[option.id] = null
        if (getVariantByOptions(mounting, product, options))
            return {...getVariantByOptions(mounting, product, options).attributes}
    }

    return null
}

export const getOptionValueLabel = (product, optionId, valueId) => {
    return product.configurableOptions
        .find(option => option.id === optionId)?.values
        .find(value => value.id === valueId)?.label
}

export const getColorSwatches = product => {
    return product.configurableOptions
        ?.find(option => ATT_ID_COLOR.includes(option.id))
        ?.values.map(value => {
            return {
                label: value.label,
                swatchUrl: value.swatch,
            }
        }) ?? []
}

const getChangeList = (products, mountingSurfaces, roomHeight) => {

    let filteredProducts = Object.values(products);

    // remove product if some variant property providesMountingSurface has value ("Seilsystem")
    filteredProducts = filteredProducts.filter(product => Object.values(product.variants).find(variant => !variant.providesMountingSurface))

    // remove product if some variant property minRoomHeight value is greater then roomHeight
    filteredProducts = filteredProducts.filter(product => Object.values(product.variants).find(variant => !(variant.minRoomHeight > roomHeight)))

    // remove product with missing mountingSurfaces option
    filteredProducts = filteredProducts.filter(product => product.mountingSurfaces.find(productMountingSurface => mountingSurfaces.includes(productMountingSurface)))

    return filteredProducts.map(product => {
        const variant = Object.values(product.variants)[0]
        const colorList = getColorSwatches(product)

        return {
            name: product.name,
            imageUrl: product.imageUrl,
            mountingImageCeilingUrl: product.mountingImageCeilingUrl,
            mountingImageFloorUrl: product.mountingImageFloorUrl,
            mountingImageWallUrl: product.mountingImageWallUrl,
            unitCount: variant.unitCount,
            colorCount: colorList.length,
            colorList: colorList,
            parentSku: product.sku,
            sku: variant.sku
        }
    })
}

export const getVariants = (mounting, product) => {
    if(mounting === 'wall') {
        return Object.values(product.variants).filter(variant => {
            return !ATT_ID_MOUNTING_OPTION.includes(variant.attributes[ATT_ID_MOUNTING])
        })
    }

    return  Object.values(product.variants)
}